import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'de' | 'en';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations = {
  de: {
    // Navigation
    'nav.demo': 'Kostenlose Demo',
    'nav.blog': 'Blog',
    // Hero
    'hero.badge': 'Neu: KI-gestützte Priorisierung',
    'hero.title': 'CRM Kraft – Super-Performance im Vertrieb',
    'hero.subtitle': 'Steigere Deine Vertriebseffizienz mit KI-gestützter Lead-Priorisierung, automatisiertem Follow-up und intelligenten Verkaufsprognosen.',
    'hero.customers': '110+ zufriedene Kunden',
    'hero.experience': '4+ Jahre Erfahrung',
    'hero.gdpr': 'DSGVO-konform',
    'hero.cta.start': 'Jetzt starten',
    'hero.cta.testimonials': 'Kundenstimmen',
    // Social Proof
    'social.customers': 'Zufriedene Kunden',
    'social.customers.desc': 'Vertrauen auf CRM Kraft',
    'social.experience': 'Jahre Erfahrung',
    'social.experience.desc': 'Im Vertriebsautomatisierung',
    'social.revenue': 'Zusätzlicher Umsatz',
    'social.revenue.desc': 'Für unsere Kunden generiert',
    'social.satisfaction': 'Kundenzufriedenheit',
    'social.satisfaction.desc': 'Würden uns weiterempfehlen',
    // Feature Showcase
    'features.title': 'Vereinfache und optimiere mit CRM Kraft Deinen Berufsalltag!',
    'features.subtitle': 'Mit intelligenter Automatisierung und KI-gestützten Insights verwandeln Sie Ihre Vertriebsprozesse in eine effiziente Maschine.',
    'features.ai.title': 'KI-Priorisierung',
    'features.ai.desc': 'Automatische Bewertung und Priorisierung Ihrer Leads basierend auf Erfolgswahrscheinlichkeit.',
    'features.targeting.title': 'Zielgenaue Ansprache',
    'features.targeting.desc': 'Personalisierte Kommunikationsvorschläge für jeden Kontakt.',
    'features.forecast.title': 'Umsatzprognosen',
    'features.forecast.desc': 'Präzise Vorhersagen für Ihre Pipeline und Quartalsziele.',
    // Priorisierung Section
    'prio.title': 'Priorisierung und Follow-up',
    'prio.subtitle': 'Maximiere Deine Vertriebseffizienz mit intelligenter Priorisierung',
    'prio.realtime.title': 'Echtzeit-Priorisierung',
    'prio.realtime.desc': 'Unsere KI analysiert kontinuierlich Deine Leads und passt die Prioritäten automatisch an aktuelle Interaktionen und Verhaltensweisen an.',
    'prio.followup.title': 'Erinnerndes Follow-up-System',
    'prio.followup.desc': 'Verpasse nie wieder den optimalen Zeitpunkt für einen Follow-up. Automatische Erinnerungen basierend auf Lead-Aktivität.',
    'prio.focus.title': 'Fokus: Mehr Abschlüsse, weniger Aufwand',
    'prio.focus.desc': 'Konzentriere Dich auf die Leads mit der höchsten Abschlusswahrscheinlichkeit und steigere Deine Conversion-Rate.',
    'prio.time.title': 'Zeitersparnis durch Automatisierung',
    'prio.time.desc': 'Reduziere manuelle Aufgaben um bis zu 60% und investiere Deine Zeit in wertschöpfende Aktivitäten.',
    'prio.cta': 'Kostenlose Demo buchen',
    // Stats Section
    'stats.title': 'Deine Hindernisse, unsere Lösung',
    'stats.subtitle': 'Mit den richtigen Tools können Sie Ihre Vertriebsziele nicht nur erreichen, sondern übertreffen. Hier sind die Ergebnisse unserer Kunden.',
    'stats.conversions': 'mehr Abschlüsse',
    'stats.conversions.desc': 'Durchschnittliche Steigerung der Abschlussrate unserer Kunden',
    'stats.time': 'Zeitersparnis',
    'stats.time.desc': 'Bei der Lead-Qualifizierung durch automatische Priorisierung',
    'stats.revenue': 'mehr Umsatz',
    'stats.revenue.desc': 'Umsatzsteigerung im ersten Jahr der Nutzung',
    // Was Ist Section
    'about.title': 'Was ist CRM Kraft?',
    'about.text1': 'Wir sind Dein CRM-Partner – mit über 4 Jahren Erfahrung, mehr als 110 erfolgreichen Kunden und über €7 Millionen zusätzlichem Umsatz, den wir für unsere Kunden generiert haben. Wir kombinieren modernste KI-Technologie mit bewährten Vertriebsmethoden, um Dir einen entscheidenden Wettbewerbsvorteil zu verschaffen. Unser System lernt kontinuierlich aus Deinen Daten und optimiert automatisch Deine Vertriebsprozesse – von der Lead-Generierung bis zum Abschluss.',
    'about.text2': 'Entwickelt von Vertriebsexperten für Vertriebsteams, die mehr erreichen wollen. Keine komplizierten Setups, keine steile Lernkurve – einfach bessere Ergebnisse vom ersten Tag an.',
    'about.cta': 'Kostenlose Demo buchen',
    // Vorteile Section
    'benefits.title': 'Deine Vorteile durch die Implementierung des richtigen CRM-Systems:',
    'benefits.revenue.title': 'Mehr Umsatz',
    'benefits.revenue.desc': 'Steigere Deine Abschlussrate durch intelligente Lead-Priorisierung',
    'benefits.time.title': 'Weniger Zeitaufwand',
    'benefits.time.desc': 'Automatisiere repetitive Aufgaben und spare bis zu 60% Zeit',
    'benefits.relationships.title': 'Bessere Kundenbeziehungen',
    'benefits.relationships.desc': 'Verpasse nie wieder einen Follow-up und baue stärkere Beziehungen auf',
    'benefits.transparency.title': 'Volle Transparenz',
    'benefits.transparency.desc': 'Behalte jederzeit den Überblick über Deine gesamte Sales-Pipeline',
    'benefits.scalability.title': 'Skalierbarkeit',
    'benefits.scalability.desc': 'Wachse ohne Dein Team massiv ausbauen zu müssen',
    'benefits.data.title': 'Datenbasierte Entscheidungen',
    'benefits.data.desc': 'Triff bessere Entscheidungen mit präzisen Verkaufsprognosen',
    // Capabilities Section
    'capabilities.title': 'CRM Kraft-Fähigkeiten, Dein Wettbewerbsvorteil',
    'capabilities.subtitle': 'Entdecken Sie die Funktionen, die Ihren Vertrieb auf das nächste Level bringen',
    'capabilities.automated.title': 'Automatisiert',
    'capabilities.automated.desc': 'Automatische Lead-Erfassung, Qualifizierung und Zuweisung ohne manuellen Aufwand.',
    'capabilities.analytics.title': 'Präzise Analysen',
    'capabilities.analytics.desc': 'Detaillierte Einblicke in Ihre Pipeline mit Echtzeit-Dashboards und Prognosen.',
    'capabilities.data.title': 'Einfache Daten-Analyse',
    'capabilities.data.desc': 'Alle Kundendaten an einem Ort, intelligent verknüpft und leicht zugänglich.',
    'capabilities.team.title': 'Team-Kollaboration',
    'capabilities.team.desc': 'Nahtlose Zusammenarbeit im Team mit geteilten Pipelines und Aktivitäten.',
    'capabilities.security.title': 'Höchste Datensicherheit',
    'capabilities.security.desc': 'DSGVO-konform mit Enterprise-Grade Sicherheit und deutschen Servern.',
    'capabilities.speed.title': 'Blitzschnell',
    'capabilities.speed.desc': 'Schnelle Implementierung und intuitive Bedienung für sofortige Produktivität.',
    'capabilities.cta': 'Kostenlose Demo buchen',
    // Comparison Section
    'comparison.title': '7 überzeugende Gründe für CRM Kraft',
    'comparison.subtitle': 'Vergleiche selbst und sieh, warum führende Unternehmen zu uns wechseln',
    'comparison.feature': 'Feature',
    'comparison.optimization': 'CRM Optimierung durch',
    'comparison.ai': 'KI-gestützte Lead-Priorisierung',
    'comparison.followup': 'Automatisches Follow-up',
    'comparison.gdpr': 'Deutsche Server (DSGVO)',
    'comparison.forecast': 'Echtzeit-Verkaufsprognosen',
    'comparison.contacts': 'Unbegrenzte Kontakte',
    'comparison.support': 'Persönlicher Support',
    'comparison.costs': 'Keine versteckten Kosten',
    // Testimonials
    'testimonials.badge': 'Über 110+ erfolgreiche Kunden',
    'testimonials.title': 'Kundenstimmen',
    'testimonials.subtitle': 'Das sagen unsere Kunden über CRM Kraft',
    // CTA Section
    'cta.title': 'Jetzt kostenlose Demo buchen',
    'cta.subtitle': 'Überzeuge Dich selbst von der Leistungsfähigkeit von CRM Kraft. Unsere Experten zeigen Dir, wie Du Deinen Vertrieb auf das nächste Level bringst.',
    'cta.benefit1': 'Kostenlose Erstberatung',
    'cta.benefit2': 'Keine Kreditkarte erforderlich',
    'cta.benefit3': 'Persönliches Onboarding',
    'cta.button': 'Kostenlose Demo buchen',
    // Footer
    'footer.tagline': 'Super-Performance im Vertrieb mit KI-gestütztem CRM.',
    'footer.product': 'Produkt',
    'footer.features': 'Features',
    'footer.company': 'Unternehmen',
    'footer.about': 'Über uns',
    'footer.contact': 'Kontakt',
    'footer.blog': 'Blog',
    'footer.legal': 'Rechtliches',
    'footer.privacy': 'Datenschutz',
    'footer.imprint': 'Impressum',
    'footer.copyright': '© {year} CRM Kraft. Alle Rechte vorbehalten.'
  },
  en: {
    // Navigation
    'nav.demo': 'Free Demo',
    'nav.blog': 'Blog',
    // Hero
    'hero.badge': 'New: AI-powered Prioritization',
    'hero.title': 'CRM Kraft – Super-Performance in Sales',
    'hero.subtitle': 'Boost your sales efficiency with AI-powered lead prioritization, automated follow-up, and intelligent sales forecasts.',
    'hero.customers': '110+ satisfied customers',
    'hero.experience': '4+ years experience',
    'hero.gdpr': 'GDPR compliant',
    'hero.cta.start': 'Get Started',
    'hero.cta.testimonials': 'Customer Stories',
    // Social Proof
    'social.customers': 'Satisfied Customers',
    'social.customers.desc': 'Trust CRM Kraft',
    'social.experience': 'Years Experience',
    'social.experience.desc': 'In sales automation',
    'social.revenue': 'Additional Revenue',
    'social.revenue.desc': 'Generated for our customers',
    'social.satisfaction': 'Customer Satisfaction',
    'social.satisfaction.desc': 'Would recommend us',
    // Feature Showcase
    'features.title': 'Simplify and optimize your daily work with CRM Kraft!',
    'features.subtitle': 'With intelligent automation and AI-powered insights, transform your sales processes into an efficient machine.',
    'features.ai.title': 'AI Prioritization',
    'features.ai.desc': 'Automatic evaluation and prioritization of your leads based on success probability.',
    'features.targeting.title': 'Targeted Approach',
    'features.targeting.desc': 'Personalized communication suggestions for every contact.',
    'features.forecast.title': 'Revenue Forecasts',
    'features.forecast.desc': 'Precise predictions for your pipeline and quarterly goals.',
    // Priorisierung Section
    'prio.title': 'Prioritization and Follow-up',
    'prio.subtitle': 'Maximize your sales efficiency with intelligent prioritization',
    'prio.realtime.title': 'Real-time Prioritization',
    'prio.realtime.desc': 'Our AI continuously analyzes your leads and automatically adjusts priorities based on current interactions and behaviors.',
    'prio.followup.title': 'Smart Follow-up System',
    'prio.followup.desc': 'Never miss the optimal time for a follow-up. Automatic reminders based on lead activity.',
    'prio.focus.title': 'Focus: More Deals, Less Effort',
    'prio.focus.desc': 'Focus on leads with the highest closing probability and increase your conversion rate.',
    'prio.time.title': 'Time Savings Through Automation',
    'prio.time.desc': 'Reduce manual tasks by up to 60% and invest your time in value-creating activities.',
    'prio.cta': 'Book Free Demo',
    // Stats Section
    'stats.title': 'Your Challenges, Our Solution',
    'stats.subtitle': "With the right tools, you can not only achieve but exceed your sales goals. Here are our customers' results.",
    'stats.conversions': 'more deals',
    'stats.conversions.desc': 'Average increase in closing rate for our customers',
    'stats.time': 'time savings',
    'stats.time.desc': 'In lead qualification through automatic prioritization',
    'stats.revenue': 'more revenue',
    'stats.revenue.desc': 'Revenue increase in the first year of use',
    // Was Ist Section
    'about.title': 'What is CRM Kraft?',
    'about.text1': 'We are your CRM partner – with over 4 years of experience, more than 110 successful customers, and over €7 million in additional revenue generated for our clients. We combine cutting-edge AI technology with proven sales methods to give you a decisive competitive advantage. Our system continuously learns from your data and automatically optimizes your sales processes – from lead generation to closing.',
    'about.text2': 'Developed by sales experts for sales teams who want to achieve more. No complicated setups, no steep learning curve – simply better results from day one.',
    'about.cta': 'Book Free Demo',
    // Vorteile Section
    'benefits.title': 'Your benefits from implementing the right CRM system:',
    'benefits.revenue.title': 'More Revenue',
    'benefits.revenue.desc': 'Increase your closing rate through intelligent lead prioritization',
    'benefits.time.title': 'Less Time Required',
    'benefits.time.desc': 'Automate repetitive tasks and save up to 60% time',
    'benefits.relationships.title': 'Better Customer Relationships',
    'benefits.relationships.desc': 'Never miss a follow-up and build stronger relationships',
    'benefits.transparency.title': 'Full Transparency',
    'benefits.transparency.desc': 'Keep track of your entire sales pipeline at all times',
    'benefits.scalability.title': 'Scalability',
    'benefits.scalability.desc': 'Grow without having to massively expand your team',
    'benefits.data.title': 'Data-driven Decisions',
    'benefits.data.desc': 'Make better decisions with precise sales forecasts',
    // Capabilities Section
    'capabilities.title': 'CRM Kraft Capabilities, your competitive advantage',
    'capabilities.subtitle': 'Discover the features that take your sales to the next level',
    'capabilities.automated.title': 'Automated',
    'capabilities.automated.desc': 'Automatic lead capture, qualification, and assignment without manual effort.',
    'capabilities.analytics.title': 'Precise Analytics',
    'capabilities.analytics.desc': 'Detailed insights into your pipeline with real-time dashboards and forecasts.',
    'capabilities.data.title': 'Easy Data Analysis',
    'capabilities.data.desc': 'All customer data in one place, intelligently linked and easily accessible.',
    'capabilities.team.title': 'Team Collaboration',
    'capabilities.team.desc': 'Seamless team collaboration with shared pipelines and activities.',
    'capabilities.security.title': 'Highest Data Security',
    'capabilities.security.desc': 'GDPR compliant with enterprise-grade security and German servers.',
    'capabilities.speed.title': 'Lightning Fast',
    'capabilities.speed.desc': 'Quick implementation and intuitive operation for immediate productivity.',
    'capabilities.cta': 'Book Free Demo',
    // Comparison Section
    'comparison.title': '7 compelling reasons for CRM Kraft',
    'comparison.subtitle': 'Compare for yourself and see why leading companies are switching to us',
    'comparison.feature': 'Feature',
    'comparison.optimization': 'CRM Optimization by',
    'comparison.ai': 'AI-powered lead prioritization',
    'comparison.followup': 'Automatic follow-up',
    'comparison.gdpr': 'German servers (GDPR)',
    'comparison.forecast': 'Real-time sales forecasts',
    'comparison.contacts': 'Unlimited contacts',
    'comparison.support': 'Personal support',
    'comparison.costs': 'No hidden costs',
    // Testimonials
    'testimonials.badge': 'Over 110+ successful customers',
    'testimonials.title': 'Customer Stories',
    'testimonials.subtitle': 'What our customers say about CRM Kraft',
    // CTA Section
    'cta.title': 'Book Your Free Demo Now',
    'cta.subtitle': 'See for yourself the power of CRM Kraft. Our experts will show you how to take your sales to the next level.',
    'cta.benefit1': 'Free initial consultation',
    'cta.benefit2': 'No credit card required',
    'cta.benefit3': 'Personal onboarding',
    'cta.button': 'Book Free Demo',
    // Footer
    'footer.tagline': 'Super-performance in sales with AI-powered CRM.',
    'footer.product': 'Product',
    'footer.features': 'Features',
    'footer.company': 'Company',
    'footer.about': 'About us',
    'footer.contact': 'Contact',
    'footer.blog': 'Blog',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacy',
    'footer.imprint': 'Imprint',
    'footer.copyright': '© {year} CRM Kraft. All rights reserved.'
  }
};
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return saved as Language || 'de';
  });
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };
  const t = (key: string): string => {
    const translation = translations[language][key];
    if (!translation) {
      console.warn(`Missing translation for key: ${key}`);
      return key;
    }
    // Handle dynamic values like {year}
    return translation.replace('{year}', new Date().getFullYear().toString());
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-340">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}